import React, { useState, useEffect } from 'react';
import { Shield, Save, Loader2, ToggleLeft, ToggleRight, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../../config/apiBase';


const PROVIDERS = [
    {
        key: 'google',
        name: 'Google',
        color: '#4285F4',
        icon: (
            <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
        ),
        fields: [],
        helpUrl: 'https://console.cloud.google.com/apis/credentials',
        helpText: 'Configurado via Google Auth. Nenhuma acao necessaria.',
    },
    {
        key: 'facebook',
        name: 'Facebook',
        color: '#1877F2',
        icon: (
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
        ),
        fields: [
            { key: 'app_id', label: 'App ID', placeholder: 'Cole seu Facebook App ID' },
            { key: 'app_secret', label: 'App Secret', placeholder: 'Cole seu Facebook App Secret', secret: true },
            { key: 'redirect_url', label: 'Redirect URL', placeholder: 'https://seudominio.com/auth/facebook/callback' },
        ],
        helpUrl: 'https://developers.facebook.com/apps',
        helpText: 'Crie um app em developers.facebook.com e configure o OAuth.',
    },
    {
        key: 'apple',
        name: 'Apple',
        color: '#ffffff',
        icon: (
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor"><path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/></svg>
        ),
        fields: [
            { key: 'service_id', label: 'Service ID', placeholder: 'com.seuapp.auth' },
            { key: 'key_id', label: 'Key ID', placeholder: 'Cole seu Key ID' },
            { key: 'team_id', label: 'Team ID', placeholder: 'Cole seu Team ID' },
        ],
        helpUrl: 'https://developer.apple.com/account/resources/identifiers/list',
        helpText: 'Requer Apple Developer Account ($99/ano). Configure Service ID e Key.',
    },
];

export default function SocialLoginSettings() {
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const token = localStorage.getItem('token');
    const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

    useEffect(() => {
        fetch(`${API}/settings/social-login/admin`, { headers })
            .then(r => r.json())
            .then(data => {
                if (data.providers) setSettings(data.providers);
                else setSettings({
                    google: { enabled: true, configured: true },
                    facebook: { enabled: false, configured: false, app_id: '', app_secret: '', redirect_url: '' },
                    apple: { enabled: false, configured: false, service_id: '', key_id: '', team_id: '' },
                });
            })
            .catch(() => toast.error('Erro ao carregar configurações'))
            .finally(() => setLoading(false));
    }, []);

    const updateField = (provider, field, value) => {
        setSettings(prev => ({
            ...prev,
            [provider]: { ...prev[provider], [field]: value }
        }));
    };

    const toggleProvider = (provider) => {
        const cfg = settings[provider];
        if (!cfg.configured && !cfg.enabled) {
            toast.error('Configure as credenciais antes de ativar');
            return;
        }
        updateField(provider, 'enabled', !cfg.enabled);
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await fetch(`${API}/settings/social-login`, {
                method: 'PUT', headers,
                body: JSON.stringify({ providers: settings })
            });
            if (res.ok) {
                const data = await res.json();
                if (data.providers) setSettings(data.providers);
                toast.success('Configurações salvas!');
            } else toast.error('Erro ao salvar');
        } catch { toast.error('Erro de conexão'); }
        finally { setSaving(false); }
    };

    if (loading) return (
        <div className="sv-page flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin" style={{ color: 'var(--sv-gold-primary)' }} />
        </div>
    );

    return (
        <div className="sv-page" data-testid="social-login-settings">
            <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-3">
                    <Shield size={20} style={{ color: 'var(--sv-gold-primary)' }} />
                    <h2 className="text-lg font-bold" style={{ color: 'var(--text)', fontFamily: "var(--font-display)" }}>Login Social</h2>
                </div>
                <button className="sv-btn-gold-system sv-btn-system" onClick={handleSave} disabled={saving} data-testid="save-social-settings">
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                    <span className="ml-1">Salvar</span>
                </button>
            </div>

            <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>
                Configure os provedores de login social. Ative apenas os que tiverem credenciais configuradas.
            </p>

            <div className="space-y-4">
                {PROVIDERS.map(provider => {
                    const cfg = settings?.[provider.key] || {};
                    const isEnabled = cfg.enabled;
                    const isConfigured = cfg.configured;

                    return (
                        <div key={provider.key} className="sv-card-system" style={{ padding: '16px' }} data-testid={`provider-${provider.key}`}>
                            {/* Header */}
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-xl flex items-center justify-center"
                                        style={{ background: 'var(--surface-2)', border: '1px solid var(--border)' }}>
                                        {provider.icon}
                                    </div>
                                    <div>
                                        <p className="text-sm font-semibold" style={{ color: 'var(--text)' }}>{provider.name}</p>
                                        <p className="text-[10px]" style={{ color: isConfigured ? '#22c55e' : 'var(--muted-2)' }}>
                                            {isConfigured ? 'Configurado' : 'Não configurado'}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => toggleProvider(provider.key)}
                                    className="flex items-center gap-1.5 transition-colors"
                                    data-testid={`toggle-${provider.key}`}
                                >
                                    {isEnabled ? (
                                        <ToggleRight size={32} style={{ color: '#22c55e' }} />
                                    ) : (
                                        <ToggleLeft size={32} style={{ color: 'var(--muted-2)' }} />
                                    )}
                                    <span className="text-xs" style={{ color: isEnabled ? '#22c55e' : 'var(--muted-2)' }}>
                                        {isEnabled ? 'Ativo' : 'Inativo'}
                                    </span>
                                </button>
                            </div>

                            {/* Help text */}
                            <p className="text-xs mb-3" style={{ color: 'var(--muted)' }}>
                                {provider.helpText}
                                {provider.helpUrl && (
                                    <a href={provider.helpUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 ml-1" style={{ color: 'var(--sv-gold-primary)' }}>
                                        Abrir console <ExternalLink size={10} />
                                    </a>
                                )}
                            </p>

                            {/* Fields */}
                            {provider.fields.length > 0 && (
                                <div className="space-y-2">
                                    {provider.fields.map(field => (
                                        <div key={field.key}>
                                            <label className="text-[10px] font-medium mb-1 block" style={{ color: 'var(--muted-2)' }}>{field.label}</label>
                                            <input
                                                type={field.secret ? 'password' : 'text'}
                                                value={cfg[field.key] || ''}
                                                onChange={e => updateField(provider.key, field.key, e.target.value)}
                                                placeholder={field.placeholder}
                                                className="w-full px-3 py-2 rounded-lg text-xs"
                                                style={{ background: 'var(--surface-2)', border: '1px solid var(--border)', color: 'var(--text)', outline: 'none' }}
                                                data-testid={`input-${provider.key}-${field.key}`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
